/**
 * File-type validator for contract document uploads.
 *
 * Lives next to {@link formatFileSize} so the upload zone and the
 * upload section can share the same allow-list. Centralizing the rule
 * here keeps the user-visible rejection message consistent across web
 * and native platforms.
 * @module features/contracts/utils/validateContractFile
 */

import { CONTRACT_ACCEPTED_MIME_TYPES } from "../constants";

/** Result returned by {@link validateContractFile}. */
export interface ContractFileValidationResult {
  readonly valid: boolean;
  readonly reason?: string;
}

/** Minimum file shape needed for validation. */
export interface ContractFileLike {
  readonly type: string;
  readonly name?: string;
}

const ACCEPTED = new Set<string>(CONTRACT_ACCEPTED_MIME_TYPES);
const REJECTION_MESSAGE = "Only PDF, JPEG, and PNG files are supported.";

/**
 * Validates that a file's MIME type is one of the accepted contract
 * document formats.
 * @param file - File-like object with a MIME `type` and optional `name`
 * @returns `{ valid: true }` for accepted files, `{ valid: false, reason }` otherwise
 */
export function validateContractFile(
  file: ContractFileLike
): ContractFileValidationResult {
  if (!file.type || !ACCEPTED.has(file.type)) {
    return { valid: false, reason: REJECTION_MESSAGE };
  }
  return { valid: true };
}
